import { StaticPageLayout } from '../layouts/static-page-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Lightbulb, Shield, Users, HandHeart, Mail, MessageCircle, ArrowRight, BookOpen, Target, Brain } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export function AboutPage() {
  // Set page title
  useEffect(() => {
    document.title = 'About Us - AceHorizon';
  }, []);
  const coreValues = [
    {
      icon: Lightbulb,
      title: "Innovation",
      description: "We are committed to pushing the boundaries of AI to create transformative educational experiences.",
      color: "bg-blue-500",
      gradient: "from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20"
    },
    {
      icon: Users,
      title: "Accessibility",
      description: "We believe education should be accessible to everyone, breaking down barriers to learning.",
      color: "bg-green-500",
      gradient: "from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20"
    },
    {
      icon: HandHeart,
      title: "Empowerment",
      description: "Our goal is to empower students and educators with tools that unlock their full potential.",
      color: "bg-purple-500",
      gradient: "from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20"
    },
    {
      icon: Shield,
      title: "Integrity",
      description: "We prioritize ethical AI practices and ensure that our solutions are transparent and secure.",
      color: "bg-orange-500",
      gradient: "from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20"
    }
  ];

  const features = [
    {
      title: "Personalized learning experiences tailored to individual needs",
      description: "Our AI adapts to your unique learning style and pace"
    },
    {
      title: "AI-powered tools that save time and enhance productivity",
      description: "Smart automation helps you focus on what matters most"
    },
    {
      title: "Seamless integration with existing educational workflows",
      description: "Works with your current tools and processes"
    },
    {
      title: "Trusted by students, educators, and institutions worldwide",
      description: "Join thousands who have transformed their learning experience"
    }
  ];

  return (
    <StaticPageLayout>
      <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
        
        {/* Hero Section */}
        <div className="text-center py-5 sm:py-12">
          <div className="max-w-4xl mx-auto px-2 sm:px-4">
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold tracking-tight mb-6 bg-gradient-to-r from-cyan-500 via-blue-600 to-purple-600 dark:from-cyan-400 dark:via-blue-500 dark:to-purple-500 bg-clip-text text-transparent leading-tight px-2">
              About Ace Horizon
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed px-2">
              At Ace Horizon, we are revolutionizing the way students and educationists interact with AI. Our mission is to empower learners and educators with 
              <span className="text-primary font-semibold"> cutting-edge AI tools</span> that make studying, teaching, and knowledge-sharing more 
              <span className="text-primary font-semibold"> efficient, engaging, and impactful</span>.
            </p>
          </div>
        </div>

        {/* Mission & Vision Section */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-transparent dark:from-blue-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
            <CardHeader className="relative z-10">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mb-4">
                <Target className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">Our Mission</CardTitle>
            </CardHeader>
            <CardContent className="relative z-10">
              <p className="text-muted-foreground leading-relaxed">
                Our mission is to bridge the gap between education and technology by providing an AI-powered agent that simplifies learning and teaching. We aim to make education accessible, personalized, and effective for everyone, regardless of their background or location.
              </p>
            </CardContent>
          </Card>

          <Card className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20">
            <div className="absolute inset-0 bg-gradient-to-r from-green-50/50 to-transparent dark:from-green-900/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
            <CardHeader className="relative z-10">
              <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mb-4">
                <Brain className="h-6 w-6 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold">Our Vision</CardTitle>
            </CardHeader>
            <CardContent className="relative z-10">
              <p className="text-muted-foreground leading-relaxed">
                We envision a future where AI becomes an integral part of the education ecosystem, enabling students to learn smarter and educators to teach better. Our vision is to create a world where knowledge is limitless, and learning is a seamless experience powered by AI.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Core Values Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 dark:from-emerald-400 dark:via-teal-400 dark:to-cyan-400 bg-clip-text text-transparent">Our Core Values</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              These fundamental principles guide everything we do at Ace Horizon
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20 text-center">
                  <div className={`absolute inset-0 bg-gradient-to-r ${value.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none`}></div>
                  <CardContent className="flex flex-col items-center justify-center p-6 relative z-10">
                    <div className={`w-16 h-16 ${value.color} rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <CardTitle className="text-xl font-semibold mb-3">{value.title}</CardTitle>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Why Choose Ace Horizon Section */}
        <div className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 dark:from-violet-400 dark:via-purple-400 dark:to-fuchsia-400 bg-clip-text text-transparent">Why Choose Ace Horizon?</h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Discover what makes our platform the preferred choice for learners worldwide
            </p>
          </div>
          
          <div className="grid gap-6 md:grid-cols-2">
            {features.map((feature, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-2 hover:border-primary/20">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                <CardContent className="p-6 relative z-10">
                  <div className="flex items-start space-x-4">
                    <div className="w-2 h-2 bg-primary rounded-full mt-3 flex-shrink-0"></div>
                    <div>
                      <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors duration-300">
                        {feature.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Get in Touch Section */}
        <div className="bg-gradient-to-r from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl p-8 text-center mb-8">
          <div className="max-w-2xl mx-auto">
            <MessageCircle className="w-16 h-16 mx-auto text-primary mb-6" />
            <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-indigo-600 via-blue-600 to-teal-600 dark:from-indigo-400 dark:via-blue-400 dark:to-teal-400 bg-clip-text text-transparent">Get in Touch</h2>
            <p className="text-lg mb-8 text-muted-foreground leading-relaxed">
              Have questions or want to learn more about Ace Horizon? We'd love to hear from you and help you discover how our platform can transform your learning experience.
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button asChild size="lg" className="px-8 py-3">
                <a href="mailto:support@acehorizon.ai">
                  <Mail className="mr-2 h-5 w-5" />
                  Email Us
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="px-8 py-3">
                <Link to="/contact">
                  Contact Page
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground mt-6">
              Reach out to us at{" "}
              <a
                href="mailto:support@acehorizon.ai"
                className="text-primary hover:underline font-medium"
              >
                support@acehorizon.ai
              </a>
            </p>
          </div>
        </div>
      </div>
    </StaticPageLayout>
  );
}
